import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { SearchService } from '@app/shared/services/search/search.service';
import { Store, select } from '@ngrx/store';
import * as loginSelectors from '@app/root-store/login-store/login.selector';
import { Subscription, interval } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'sync-user-dialog',
  templateUrl: './sync-user-dialog.component.html',
  styleUrls: ['./sync-user-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncUserDialogComponent implements OnInit, OnDestroy {
  searchInput = '';
  userInput = new UntypedFormControl('');
  delay = 300;
  searchResults = [];
  search_val = '';
  subscription = new Subscription();
  currentUser = null;
  isLoading = false;
  selectedUser: any = null;

  constructor(
    public dialogRef: MatDialogRef<SyncUserDialogComponent>,
    private searchService: SearchService,
    private cd: ChangeDetectorRef,
    private store: Store,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe((result) => {
        if (result) {
          this.currentUser = result;
        }
        this.cd.detectChanges();
      })
    );

    this.subscription.add(
      this.userInput.valueChanges
        .pipe(
          debounce(() => {
            this.isLoading = true;
            return interval(this.delay);
          })
        )
        .subscribe((value) => {
          this.isLoading = true;
          if (value.trim() == '') {
            this.searchResults = [];
            this.search_val = '';
            this.isLoading = false;
            this.cd.detectChanges();
            return;
          } else {
            this.isLoading = true;
            this.search_val = value;
            const payload = {
              search_type: 'all',
              search_val: value,
              user_id: this.currentUser?.id,
            };
            this.subscription.add(
              this.searchService.search(payload).subscribe((res) => {
                const results = res.data ? res.data : [];
                const selectedUserId = this.selectedUser?.user_id;
                this.searchResults = selectedUserId ? results.filter((user) => user?.user_id !== selectedUserId) : results;
                this.isLoading = false;
                this.cd.detectChanges();
              })
            );
          }
        })
    );
  }

  handleRelationshipSearch(value) {
    this.userInput.setValue(value);
  }

  selectUser(user) {
    if (this.selectedUser) {
      // Show confirmation dialog before replacing
      const confirmationDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Change User?',
          materialIcon: 'warning',
          text: 'A user has already been selected, do you want to replace the selected user?',
          proceedTitle: 'Change',
          showCancelBtn: true,
        },
      });

      this.subscription.add(
        confirmationDialogRef.afterClosed().subscribe((confirmation) => {
          if (confirmation?.confirmation) {
            // User confirmed, replace with new user
            this.selectedUser = user;
            this.searchResults = [];
            this.search_val = '';
            this.userInput.setValue('');
            this.cd.detectChanges();
          }
        })
      );
    } else {
      // No user selected, just select the new one
      this.selectedUser = user;
      this.searchResults = [];
      this.search_val = '';
      this.userInput.setValue('');
      this.cd.detectChanges();
    }
  }

  clearUser() {
    this.selectedUser = null;
    this.cd.detectChanges();
  }

  saveSelection() {
    if (this.selectedUser && this.selectedUser.user_id) {
      this.dialogRef.close({
        selectedUser: this.selectedUser,
        confirmation: true,
      });
    }
  }

  closeDialog() {
    this.dialogRef.close({ confirmation: false });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
